import { formatMoney } from "./lib/formatMoney.js";
import { fetchJson } from "./lib/fetchJson.js";
import { fetchCart } from "./lib/cartApi.js";
import { captureError } from "./lib/sentry.js";
import type { Cart } from "./types/Shopify.js";

interface Address {
  company_name?: string
  address1?: string
  address2?: string
  city?: string
  province?: string
  postal_code?: string
  country?: string
}
interface Rate {
  service_code: string
  service_name: string
  description: string
  total_price: number
  currency: string
}
class FFEstimate extends HTMLDialogElement{

  working = false
  fetchingRates = false
  step = 1

  address: Address = {}
  step1Valid = false
  step2Valid = false

  cellHeight = 384

  rates: Rate[] = []
  rateError = false
  rateSuccess = false
  cartEmpty = false
  selectedRate?: string
  estimateError = false
  estimateSuccess = false
  estimateUrl?: string
  preparingEstimate = false
  cartData: Pick<Cart, 'items'> = { items: [] }

  DOM: {
    next: HTMLElement | null
    prev: HTMLElement | null
    addressFields: NodeListOf<HTMLInputElement>
    indicator: HTMLElement | null
    scrollContainer: HTMLElement | null
    shippingContainer: HTMLElement | null
    resultContainer: HTMLElement | null
    spinner: HTMLElement | null

    noteInput: HTMLInputElement | null
    poNumberInput: HTMLInputElement | null
  }

  constructor(){
    super();
    this.DOM = {
      next: this.querySelector(".ffjs-next"),
      prev: this.querySelector(".ffjs-prev"),
      addressFields: this.querySelectorAll(".ffjs-addressField"),
      indicator: this.querySelector(".ffjs-stepIndicator"),
      scrollContainer: this.querySelector(".ffjs-cells"),
      shippingContainer: this.querySelector(".ffjs-shippingOptions"),
      resultContainer: this.querySelector(".ffjs-result"),
      spinner: this.querySelector(".ffjs-spinner"),

      noteInput: document.querySelector(`textarea[name="note"]`),
      poNumberInput: document.querySelector(`input[name="attributes[PO-Number]"]`),
    }
    this.events()
    this.readStoredValues()
  }

  events(){
    this.addEventListener("click", this.dialogClick)
    this.DOM.next?.addEventListener("click", this.nextClick.bind(this))
    this.DOM.prev?.addEventListener("click", this.prevClick.bind(this))
    for( const field of this.DOM.addressFields ){
      field.addEventListener("input", this.addressChange.bind(this))
      field.addEventListener("change", this.addressChange.bind(this))
    }
  }

  dialogClick(e: MouseEvent){
    const rect = this.getBoundingClientRect();
    const isInDialog = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX && e.clientX <= rect.left + rect.width);
    if (!isInDialog) {
      this.close();
    }
  }

  readStoredValues(){
    const storedAddress = localStorage.getItem("ff-estimate-address")
    if( storedAddress ){
      try {
        const address = JSON.parse(storedAddress);
        this.address = address
        for (let i = 0; i < this.DOM.addressFields.length; i++) {
          const key = this.DOM.addressFields[i].name
          if( Object.hasOwn(address, key) ){
            this.DOM.addressFields[i].value = address[key]
          }
        }
        this.addressChange()
        this.updateDisplay()
      } catch(_e) {
        // Ignore malformed saved address JSON
      }
    }
  }

  nextClick(){
    if( this.step === 1 ){
      if( this.step1Valid ){
        this.step = this.step + 1
        this.requestRates()
      }
    }else if( this.step === 2 ){
      if( this.step2Valid ){
        this.step = this.step + 1
        this.requestEstimate()
      }
    }
    this.updateDisplay()
  }

  prevClick(){
    if( this.step === 1 ) return
    this.step = this.step - 1
    this.updateDisplay()
  }

  addressChange(){
    let valid = true
    const address: Address = {}
    for (let i = 0; i < this.DOM.addressFields.length; i++) {
      const name = this.DOM.addressFields[i].name as keyof Address
      address[name] = this.DOM.addressFields[i].value
      if( this.DOM.addressFields[i].required && !this.DOM.addressFields[i].value){
        const att = document.createAttribute("required");
        this.DOM.addressFields[i].setAttributeNode(att);
        valid = false
      } 
    }

    this.address = address
    localStorage.setItem("ff-estimate-address", JSON.stringify(address))
    this.rates = []
    this.selectedRate = undefined
    this.step1Valid = valid
    this.step2Valid = false
    this.updateDisplay()
  }

  updateDisplay(){

    if( this.step === 1 ){
      if( this.step1Valid ){
        this.DOM.next?.classList.remove("js-disabled")
      }else{
        this.DOM.next?.classList.add("js-disabled")
      }
    }

    if( this.step === 2 ){
      if( this.step2Valid ){
        this.DOM.next?.classList.remove("js-disabled")
      }else{
        this.DOM.next?.classList.add("js-disabled")
      }
    }

    if( this.step > 1 ){
      this.DOM.prev?.classList.remove("js-disabled")
    }else{
      this.DOM.prev?.classList.add("js-disabled")
    }

    if( this.step === 3 ){
      this.DOM.next?.classList.add("js-disabled")
    }

    this.DOM.indicator!.innerHTML = `STEP ${this.step} OF 3`
    this.DOM.scrollContainer!.scrollTop = this.cellHeight * (this.step - 1)

    if( !this.fetchingRates ){
      if( this.cartEmpty ){
        this.step2Valid = false
        this.DOM.shippingContainer!.innerHTML = `
          <div class="message error">
            <h3 class="messageHl">Your cart is empty</h3>
            <p class="messageDesc">Add items to your cart before creating a quote.</p>
          </div>
        `
      }else if( this.rateError ){
        this.step2Valid = false
        this.DOM.shippingContainer!.innerHTML = `
          <div class="message error">
            <h3 class="messageHl">Could not load your cart</h3>
            <p class="messageDesc">Refresh the page and try again. If this keeps happening, contact our <a href="https://freeflysystems.com/contact">support team</a>.</p>
          </div>
        `
      }else if( this.rates.length ){
        this.DOM.shippingContainer!.innerHTML = ""
        for (let i = 0; i < this.rates.length; i++) {
          const rate = this.rates[i];
          const rateEl = document.createElement('div')
          rateEl.classList.add("shippingOption", "ffjs-shippingOption")
          rateEl.dataset.shipping_id = rate.service_code
          rateEl.innerHTML = `
            <div class="shippingIndicator"></div>
            <div class="shippingText">
              <p class="shippingTitle">${ rate.service_name }</p>
              <p class="shippingDescription">${ rate.description }</p>
            </div>
            <p class="shippingPrice">${ formatMoney( rate.total_price, "${{amount}}" ) }</p>
          `
          rateEl.addEventListener("click", this.rateClick.bind(this) )
          this.DOM.shippingContainer?.appendChild(rateEl)
        }
      }else{
        this.step2Valid = true
        this.DOM.shippingContainer!.innerHTML = `
          <div class="message error">
            <h3 class="messageHl">No shipping rates were returned</h3>
            <p class="messageDesc">There was likely an issue with the address you entered. You can click &ldquo;Next&rdquo; but your order may differ from the quote.</p>
          </div>
        `
      }
    }else{
      this.DOM.shippingContainer!.innerHTML = `
        <div class="message">
          <h3 class="messageHl">Retrieving shipping rates</h3>
          <p class="messageDesc">This may take a moment.</p>
        </div>
      `
    }

    if( this.selectedRate ){
      const opts = this.querySelectorAll(".ffjs-shippingOption")
      for (let i = 0; i < opts.length; i++) {
        opts[i].classList.remove("js-selected")
      }
      const selected = this.querySelector(`.ffjs-shippingOption[data-shipping_id=${this.selectedRate}]`)
      if( selected ) selected.classList.add("js-selected")
    }

    if( this.working ){
      this.DOM.spinner?.classList.add("js-active")
    }else{
      this.DOM.spinner?.classList.remove("js-active")
    }
  
    if( this.preparingEstimate ){
      this.DOM.resultContainer!.innerHTML = `
        <div class="message">
          <h3 class="messageHl">We're preparing your estimate</h3>
          <p class="messageDesc">This may take a moment.</p>
        </div>
      `
    }else if( this.estimateError ){
      this.DOM.resultContainer!.innerHTML = `
        <div class="message error">
          <h3 class="messageHl">Uh oh!</h3>
          <p class="messageDesc">Something went wrong. Contact our <a href="https://freeflysystems.com/contact">support team</a> and we can get this sorted out.</p>
        </div>
      `
    }else if( this.estimateSuccess ){
      this.DOM.resultContainer!.innerHTML = `
        <div class="message success">
          <h3 class="messageHl">Success!</h3>
          <p class="messageDesc">Your estimate is ready to download <a href="${ this.estimateUrl }" target="_blank" rel="noopener noreferrer">here</a>.</p>
        </div>
      `
    }else{
      this.DOM.resultContainer!.innerHTML = `
        <div class="message">
          <h3 class="messageHl">More details are needed.</h3>
        </div>
      `
    }
  }

  rateClick(e: MouseEvent){
    if( !(e.currentTarget instanceof HTMLElement) ) return
    const id = e.currentTarget?.dataset.shipping_id
    this.selectedRate = id
    this.step2Valid = true
    this.estimateUrl = undefined
    this.rateError = false
    this.rateSuccess = false
    this.updateDisplay()
  }

  async loadCart(){
    const cart = await fetchCart()
    this.cartData = cart
    this.cartEmpty = !cart.items.length
    return cart
  }

  async requestRates (){
    this.working = true
    this.fetchingRates = true
    this.rateError = false
    this.cartEmpty = false
    this.updateDisplay()

    try {
      await this.loadCart()
    } catch (_error) {
      this.rateError = true
      this.working = false
      this.fetchingRates = false
      this.updateDisplay()
      return
    }

    if( this.cartEmpty ){
      this.working = false
      this.fetchingRates = false
      this.updateDisplay()
      return
    }
  
    const data = {
      rate: {
        destination: this.address,
        items: this.cartData.items.map( el => ({ ...el, name: el.title }))
      }
    }

    try {
      const ratesData = await fetchJson<{ rates: Rate[]}>( 'https://g6tpffjbs8.execute-api.us-west-2.amazonaws.com/Prod/', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      this.rates = ratesData.rates.sort( (a,b) => a.total_price - b.total_price )
      this.rateError = false
    } catch (_error) {
      this.rateError = true
    }
    this.working = false
    this.fetchingRates = false
    this.updateDisplay()
  }

  getCartLink(){
    let cartLink = 'https://store.freeflysystems.com/cart?'
    const parts = []
    if( this.cartData.items.length ){
      parts.push({ id: "items", value: this.cartData.items.map( el => `${el.variant_id}:${el.quantity}`).join(",") })
    }
    if( this.DOM.noteInput && this.DOM.noteInput.value ){
      parts.push({ id: "note", value: this.DOM.noteInput.value })
    }
    if( this.DOM.poNumberInput && this.DOM.poNumberInput.value ){
      parts.push({ id: "attributes[PO-Number]", value: this.DOM.poNumberInput.value })
    }

    cartLink += parts.map( el => `${el.id}=${el.value}`).join("&")

    return cartLink
  }

  async requestEstimate(){
    this.working = true
    this.preparingEstimate = true
    this.estimateError = false
    this.estimateSuccess = false
    this.updateDisplay()

    try {
      await this.loadCart()
    } catch (error) {
      captureError(error, { source: 'estimate.requestQuote' })
      this.working = false
      this.preparingEstimate = false
      this.estimateError = true
      this.updateDisplay()
      return
    }

    if( this.cartEmpty ){
      this.working = false
      this.preparingEstimate = false
      this.estimateError = true
      this.updateDisplay()
      return
    }

    const data = {
      destination: this.address,
      items: this.cartData.items,
      shippingMethod: this.selectedRate ? this.rates.find( el => el.service_code === this.selectedRate ) : null,
      createdBy: window.ffCustomerEmail,
      cartLink: this.getCartLink(),
    }

    try {
      const url = "https://r9wosqmhlk.execute-api.us-west-2.amazonaws.com/Prod/issue-quote"
      const estimateData = await fetchJson<{ url?: string; filename?: string }>( url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (estimateData.url) {
        this.estimateUrl = estimateData.url
      } else if (estimateData.filename) {
        this.estimateUrl = `https://shopify-quote.s3-us-west-2.amazonaws.com/${estimateData.filename}`
      } else {
        throw new Error(`PDF could not be generated.`)
      }
      this.estimateSuccess = true
      this.estimateError = false
    } catch (error) {
      captureError(error, { source: 'estimate.requestQuote' })
      this.estimateSuccess = false
      this.estimateError = true
    }

    this.working = false
    this.preparingEstimate = false
    this.updateDisplay()
  }
}
customElements.define('ff-estimate-modal', FFEstimate, { extends: 'dialog' });

document.querySelector(".ffjs-estimateTrigger")?.addEventListener("click", () => {
  const modal = document.querySelector(".ffjs-estimateModal")
  if( !(modal instanceof FFEstimate) ) return
  modal.showModal()
})